import React from 'react';
import { X, FolderPlus, Folder as FolderIcon, CheckCircle2, Loader2, Star, Trash2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../ReduxToolkit/Hooks';
import { fetchFolders, createFolder } from '../../ReduxToolkit/FolderSlice';
import { assignToFolder, toggleFavorite, fetchFavorites } from '../../ReduxToolkit/FavoriteSlice';
import { Folder } from '../../types';

interface FolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string | number;
}

export const FolderModal: React.FC<FolderModalProps> = ({ isOpen, onClose, documentId }) => {
  const dispatch = useAppDispatch();
  const folders = useAppSelector((state: any) => state.folders.items || []) as Folder[];
  const loadingFolders = useAppSelector((state: any) => state.folders.status === 'loading');
  const favorites = useAppSelector((state: any) => state.favorites.items || []) as any[];

  const currentFav = React.useMemo(() => {
    return favorites.find((f: any) => String(f.documentId).toLowerCase() === String(documentId).toLowerCase());
  }, [favorites, documentId]);

  const isAlreadyFav = Boolean(currentFav);
  
  const [newFolderName, setNewFolderName] = React.useState('');
  const [isCreating, setIsCreating] = React.useState(false);
  const [isAssigning, setIsAssigning] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');

  React.useEffect(() => {
    if (isOpen) {
      dispatch(fetchFolders());
      dispatch(fetchFavorites());
      setSuccessMessage('');
    }
  }, [isOpen, dispatch]);

  if (!isOpen) return null;

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    
    setIsCreating(true);
    try {
      await dispatch(createFolder({ name: newFolderName.trim(), description: '' })).unwrap();
      setNewFolderName('');
      dispatch(fetchFolders());
    } catch (err) {
      console.error(err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleAssign = async (folder: Folder | 'TOUS') => {
    setIsAssigning(true);
    try {
      if (folder === 'TOUS') {
        if (!isAlreadyFav) {
          await dispatch(toggleFavorite(documentId) as any).unwrap();
        }
        setSuccessMessage("Document classé dans vos favoris généraux.");
      } else {
        await dispatch(assignToFolder({ documentId, folderId: folder.id }) as any).unwrap();
        setSuccessMessage("Document classé dans : " + folder.name);
      }
      await dispatch(fetchFavorites());
      await dispatch(fetchFolders());
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err) {
      console.error(err);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveFromFavorites = async () => {
    setIsAssigning(true);
    try {
      await dispatch(toggleFavorite(documentId) as any).unwrap();
      setSuccessMessage("Document retiré de vos favoris.");
      await dispatch(fetchFavorites());
      await dispatch(fetchFolders());
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err) {
      console.error(err);
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col relative" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="p-6 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
          <h3 className="font-display font-extrabold text-xl text-zinc-900 flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
            Ranger dans un Dossier
          </h3>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-700 bg-white hover:bg-zinc-100 rounded-full transition-colors cursor-pointer shadow-sm border border-zinc-200">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {successMessage ? (
            <div className="flex flex-col items-center justify-center py-8 text-center space-y-3 animate-in zoom-in duration-300">
              <CheckCircle2 className="w-16 h-16 text-emerald-500" />
              <p className="font-bold text-lg text-emerald-700">{successMessage}</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-zinc-500">Choisissez un classeur intellectuel pour organiser cet acte ou créez-en un nouveau.</p>

              {/* Folders List */}
              <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar pr-2">
                <button
                  type="button"
                  onClick={() => handleAssign('TOUS')}
                  disabled={isAssigning}
                  className="w-full flex items-center justify-between p-3.5 bg-white border border-zinc-200 hover:border-amber-300 hover:bg-amber-50 rounded-xl transition-all group cursor-pointer text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 text-amber-600 rounded-lg group-hover:bg-amber-200 transition-colors">
                      <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                    </div>
                    <span className="font-bold text-sm text-zinc-700 group-hover:text-amber-800">📚 Tous mes favoris (Index global)</span>
                  </div>
                  {isAlreadyFav && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                      Déjà favori
                    </span>
                  )}
                </button>

                {loadingFolders ? (
                  <div className="flex justify-center p-4"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
                ) : (
                  folders.map((folder) => {
                    const isAssignedHere = currentFav?.folderIds?.some((fid: string) => String(fid).toLowerCase() === String(folder.id).toLowerCase());
                    return (
                      <button
                        type="button"
                        key={folder.id}
                        onClick={() => handleAssign(folder)}
                        disabled={isAssigning}
                        className={`w-full flex items-center justify-between p-3.5 bg-white border rounded-xl transition-all group cursor-pointer text-left ${
                          isAssignedHere ? 'border-amber-300 bg-amber-50/50 hover:bg-amber-50' : 'border-zinc-200 hover:border-amber-300 hover:bg-amber-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg transition-colors ${isAssignedHere ? 'bg-amber-500 text-white' : 'bg-amber-100 text-amber-700 group-hover:bg-amber-200'}`}>
                            <FolderIcon className="w-4 h-4" />
                          </div>
                          <span className="font-bold text-sm text-zinc-700 group-hover:text-amber-900">{folder.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {isAssignedHere && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                              ✓ Classé ici
                            </span>
                          )}
                          {typeof folder.documentCount === 'number' && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">
                              {folder.documentCount} acte(s)
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {/* Retirer des favoris si déjà favori */}
              {isAlreadyFav && (
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={handleRemoveFromFavorites}
                    disabled={isAssigning}
                    className="w-full flex items-center justify-center gap-2 p-2.5 text-xs font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-xl transition-all border border-red-200 cursor-pointer disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Retirer cet acte de mes favoris
                  </button>
                </div>
              )}

              {/* Create New Folder */}
              <div className="pt-4 border-t border-zinc-100">
                <form onSubmit={handleCreateFolder} className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Nouveau dossier client..."
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    className="flex-1 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                  />
                  <button
                    type="submit"
                    disabled={isCreating}
                    className="bg-primary-750 hover:bg-primary-800 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                    title="Créer le dossier"
                  >
                    <FolderPlus className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
};